import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import { visuallyHidden } from "@mui/utils";
import Typography from "@mui/material/Typography";
import Toolbar from "@mui/material/Toolbar";
import { useTheme } from "@mui/material";
import ClipLoader from "react-spinners/ClipLoader";
import { useData } from "./DataProvider";


const headCells = [
  {
    id: "userId",
    disablePadding: false,
    label: "LoginId",
  },
  {
    id: "hospitals",
    disablePadding: false,
    label: "Clinic Name",
  },
  {
    id: "locations",
    disablePadding: false,
    label: "Location",
  },
  {
    id: "total_assessment_count",
    disablePadding: false,
    label: "Assessment Count",
  },
  {
    id: "welcome_assessment_count",
    disablePadding: false,
    label: "Welcome Assessment Count",
  },
  {
    id: "paid_assessment_count",
    disablePadding: false,
    label: "Paid Assessment Count",
  },
  {
    id: "free_assessment_count",
    disablePadding: false,
    label: "Free Assessment Count",
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="center"
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{ fontWeight: "bold", fontSize: "14px" }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
              sx={{
                "& .MuiTableSortLabel-icon": {
                  opacity: 0.3, 
                  marginLeft: "4px",
                },
              }}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

function EnhancedTableToolbar({ onExport }) {
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
      }}
    >
      <Box sx={{ width: '100%' }}>
        <div
          style={{
            fontFamily: "Roboto",
            fontSize: "13px",
            marginLeft: 'auto',
            padding: "10px",
            paddingLeft: '12px',
            cursor: 'pointer',
            marginRight: '20px',
            width: '60px',
            color: 'white',
            backgroundColor: '#ff950a',
            borderRadius: '6px',
          }}
          onClick={onExport}
        >
          Export
        </div>
      </Box>
    </Toolbar>
  );
}

export default function DRLDoctors(props) {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("userId");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(15);
  const { doctors, loading } = useData();

  // Preprocess doctors data to include free_assessment_count
  const rows = React.useMemo(() => {
    return doctors.map((row) => ({
      ...row,
      free_assessment_count: (row.total_assessment_count || 0) - ((row.welcome_assessment_count || 0) + (row.paid_assessment_count || 0)),
    }));
  }, [doctors]);

  React.useEffect(() => {
    console.log("Doctors Data: ", doctors);
  }, [doctors]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const visibleRows = React.useMemo(
    () =>
      stableSort(rows.length ? rows : [], getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, rows]
  );

  const handleExport = () => {
    const headers = [
      "LoginId",
      "Clinic Name",
      "Location",
      "Assessment Count",
      "Welcome Assessment Count",
      "Paid Assessment Count",
      "Free Assessment Count",
    ];

    // Convert to CSV format
    let csvContent = headers.join(",") + "\n";

    rows.forEach((row) => {
      const rowValues = [
        row.userId,
        row.hospitals,
        row.locations,
        row.total_assessment_count,
        row.welcome_assessment_count,
        row.paid_assessment_count,
        row.free_assessment_count,
      ];

      csvContent += rowValues.join(",") + "\n";
    });

    // Download the CSV
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "export.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  console.log(doctors)

  return (
    <Box sx={{ marginTop: "16px" }}>
      <Paper sx={{ width: "100%", mb: 2, marginBottom: "90px" }}>
        <EnhancedTableToolbar onExport={handleExport} />
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            {loading ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={headCells.length} align="center">
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "40vh",
                      }}
                    >
                      <ClipLoader color={"#fd8c00"} size={30} />
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {visibleRows.length > 0 ? (
                  visibleRows.map((row, index) => (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={`${row.userId}-${index}`}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell align="center" sx={{ fontSize: "13px" }}>
                        {row.userId}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: "13px" }}>
                        {row.hospitals ? row.hospitals : "-"}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: "13px" }}>
                        {row.locations ? row.locations : "-"}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: "13px" }}>
                        {row.total_assessment_count || "0"}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: "13px" }}>
                        {row.welcome_assessment_count || "0"}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: "13px" }}>
                        {row.paid_assessment_count || "0"}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: "13px" }}>
                        {row.free_assessment_count || "0"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={headCells.length} align="center">
                      No data found
                    </TableCell>
                  </TableRow>
                )}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={headCells.length} />
                  </TableRow>
                )}
              </TableBody>
            )}
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 15, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
